import {
  autoUpdate,
  offset as offsetMiddleware,
  flip as flipMiddleware,
  size as sizeMiddleware,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  Placement,
  OffsetOptions,
  UseRoleProps,
  useFloatingNodeId,
} from '@floating-ui/react'
import { useState } from 'react'
import { useUpdateEffect } from 'react-use'

type UseMenuParams = {
  opened?: boolean
  defaultOpened?: boolean
  onOpenedChange?: (opened: boolean) => void
  placement?: Placement
  offset?: OffsetOptions
  ariaRole?: UseRoleProps['role']
  disabled?: boolean
}

// eslint-disable-next-line import/prefer-default-export
export function usePopup({
  opened,
  defaultOpened,
  onOpenedChange,
  placement,
  offset,
  ariaRole,
  disabled,
}: UseMenuParams) {
  const [isOpened, setIsOpened] = useState(() => opened ?? defaultOpened ?? false)

  useUpdateEffect(() => {
    if (typeof opened !== 'undefined') {
      setIsOpened(opened)
    }
  }, [opened])

  const handleOnOpenedChange = (nextOpenedState: boolean) => {
    if (typeof opened === 'undefined') {
      setIsOpened(nextOpenedState)
    }

    onOpenedChange?.(nextOpenedState)
  }

  const nodeId = useFloatingNodeId()

  const { refs, context, floatingStyles } = useFloating({
    nodeId,
    placement,
    transform: true,
    open: isOpened,
    onOpenChange: handleOnOpenedChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware(offset),
      flipMiddleware(),
      sizeMiddleware({
        padding: 16,
        apply({ availableHeight, elements }) {
          // eslint-disable-next-line no-param-reassign
          elements.floating.style.maxHeight = `${availableHeight}px`
        },
      }),
    ],
  })

  const role = useRole(context, { role: ariaRole })
  const click = useClick(context, { enabled: !disabled })
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role])

  return {
    isOpened,
    setIsOpened: handleOnOpenedChange,
    nodeId,
    refs,
    context,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  }
}
